import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantNotificationSettings.css";
import { API_BASE } from "../../../ApiUrl";
import SettingsTabs from "../Settings/SettingsTabs";

const API_URL = `${API_BASE}/tenant-notification-settings/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const EVENT_TYPE_SUGGESTIONS = [
  "PROJECT_CREATED",
  "PROJECT_COMPLETED",
  "INSPECTION_DUE",
  "INSPECTION_OVERDUE",
  "REPORT_PUBLISHED",
  "USER_INVITED",
  "PASSWORD_RESET",
  "SUBSCRIPTION_RENEWAL",
  "STORAGE_LIMIT_REACHED",
  "SECURITY_ALERT"
];

// Convert object → array of { key, value }
const objectToRows = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return [{ key: "", value: "" }];
  }
  const entries = Object.entries(obj);
  if (!entries.length) return [{ key: "", value: "" }];
  return entries.map(([key, value]) => ({
    key,
    value: value == null ? "" : String(value)
  }));
};

// Convert array of { key, value } → { key: value }
const rowsToObject = (rows) => {
  const obj = {};
  rows.forEach((row) => {
    const k = (row.key || "").trim();
    const v = (row.value || "").trim();
    if (k) obj[k] = v;
  });
  return obj;
};

const emptyRule = () => ({
  id: null,
  event_type: "",
  channels: [],
  channel_input: "",
  escalation_rows: [{ key: "", value: "" }]
});

const AddTenantNotificationSettings = () => {
  const navigate = useNavigate();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([emptyRule()]);
  const [tenantId, setTenantId] = useState(null);
  const [userId, setUserId] = useState(null);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
    if (tenantUser?.user_id) setUserId(Number(tenantUser.user_id));
  }, []);

  useEffect(() => {
    if (tenantId) fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchRules = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_notification_settings"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_notification_settings) {
        rows = result.data.tenant_notification_settings;
      } else if (result?.tenant_notification_settings) {
        rows = result.tenant_notification_settings;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setRules(rows);
    } catch (error) {
      console.error("Error fetching notification rules:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load rules",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading rules."
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Form handlers ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [name]: value } : r))
    );
  };

  // ---- Channel chip handlers ----
  const handleChannelInputChange = (index, value) => {
    setFormData((prev) =>
      prev.map((r, i) => (i === index ? { ...r, channel_input: value } : r))
    );
  };

  const addChannelChip = (index) => {
    setFormData((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const raw = (r.channel_input || "").trim().toUpperCase();
        if (!raw) return { ...r, channel_input: "" };
        if (r.channels.includes(raw)) return { ...r, channel_input: "" };
        return {
          ...r,
          channels: [...r.channels, raw],
          channel_input: ""
        };
      })
    );
  };

  const removeChannelChip = (index, channelValue) => {
    setFormData((prev) =>
      prev.map((r, i) =>
        i === index
          ? {
              ...r,
              channels: r.channels.filter((c) => c !== channelValue)
            }
          : r
      )
    );
  };

  const handleChannelKeyDown = (index, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addChannelChip(index);
    } else if (e.key === "Backspace" && !e.target.value) {
      setFormData((prev) =>
        prev.map((r, i) => {
          if (i !== index) return r;
          if (!r.channels.length) return r;
          return { ...r, channels: r.channels.slice(0, -1) };
        })
      );
    }
  };

  // ---- Escalation row handlers ----
  const handleEscalationRowChange = (ruleIndex, rowIndex, field, value) => {
    setFormData((prev) =>
      prev.map((r, i) => {
        if (i !== ruleIndex) return r;
        const updated = r.escalation_rows.map((row, j) =>
          j === rowIndex ? { ...row, [field]: value } : row
        );
        return { ...r, escalation_rows: updated };
      })
    );
  };

  const addEscalationRow = (ruleIndex) => {
    setFormData((prev) =>
      prev.map((r, i) =>
        i === ruleIndex
          ? {
              ...r,
              escalation_rows: [
                ...r.escalation_rows,
                { key: "", value: "" }
              ]
            }
          : r
      )
    );
  };

  const removeEscalationRow = (ruleIndex, rowIndex) => {
    setFormData((prev) =>
      prev.map((r, i) => {
        if (i !== ruleIndex) return r;
        const next = r.escalation_rows.filter((_, j) => j !== rowIndex);
        return {
          ...r,
          escalation_rows: next.length
            ? next
            : [{ key: "", value: "" }]
        };
      })
    );
  };

  // ---- Row handlers ----
  const handleAdd = () => {
    setFormData([emptyRule()]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, emptyRule()]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (r) => {
    // Normalize escalation_rules into rows
    let escalationObj = {};
    if (r.escalation_rules !== null && r.escalation_rules !== undefined) {
      if (typeof r.escalation_rules === "string") {
        try {
          escalationObj = JSON.parse(r.escalation_rules);
        } catch {
          escalationObj = {};
        }
      } else if (
        typeof r.escalation_rules === "object" &&
        !Array.isArray(r.escalation_rules)
      ) {
        escalationObj = r.escalation_rules;
      }
    }

    // Normalize channels into an array
    let channelsArr = [];
    if (Array.isArray(r.channels)) {
      channelsArr = r.channels.map((c) => String(c));
    } else if (typeof r.channels === "string" && r.channels) {
      try {
        const parsed = JSON.parse(r.channels);
        if (Array.isArray(parsed)) {
          channelsArr = parsed.map((c) => String(c));
        }
      } catch {
        channelsArr = r.channels
          .split(",")
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean);
      }
    }

    setFormData([
      {
        id: r.id,
        event_type: r.event_type || "",
        channels: channelsArr,
        channel_input: "",
        escalation_rows: objectToRows(escalationObj)
      }
    ]);
    setEditingId(r.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (r) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Rule?",
      text: `Are you sure you want to delete the rule for "${
        r.event_type || "this event"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${r.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Notification rule deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchRules();
    } catch (error) {
      console.error("Error deleting notification rule:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the rule."
      });
    }
  };

  const validateRow = (r, index) => {
    const label = `Rule ${index + 1}`;

    if (!r.event_type.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Event Type Required",
        text: `Please provide an Event Type for ${label}.`
      });
      return false;
    }

    if (r.event_type.trim().length > 100) {
      Swal.fire({
        icon: "warning",
        title: "Event Type Too Long",
        text: `Event Type for ${label} must be 100 characters or fewer.`
      });
      return false;
    }

    if (!Array.isArray(r.channels) || r.channels.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Channels Entered",
        text: `Please enter at least one channel for ${label}.`
      });
      return false;
    }

    // Escalation rows: check each filled key has a value; no duplicate keys
    const escalationKeys = [];
    for (let i = 0; i < r.escalation_rows.length; i++) {
      const row = r.escalation_rows[i];
      const k = (row.key || "").trim();
      const v = (row.value || "").trim();
      if (k && !v) {
        Swal.fire({
          icon: "warning",
          title: "Missing Escalation Value",
          text: `Please provide a value for escalation key "${k}" in ${label}.`
        });
        return false;
      }
      if (k) escalationKeys.push(k.toLowerCase());
    }

    const dup = escalationKeys.findIndex(
      (k, i) => escalationKeys.indexOf(k) !== i
    );
    if (dup !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Escalation Key",
        text: `Escalation key "${r.escalation_rows[dup].key.trim()}" is used more than once in ${label}.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (r) => ({
    event_type: r.event_type.trim().toUpperCase(),
    channels: r.channels,
    escalation_rules: rowsToObject(r.escalation_rows),
    tenant: tenantId,
    user: userId
  });

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId || !userId) {
      Swal.fire({
        icon: "error",
        title: "Session missing",
        text: "Tenant ID or User ID is missing."
      });
      return;
    }

    // Commit any pending channel input into the chips before validating
    const committed = formData.map((r) => {
      const pending = (r.channel_input || "").trim().toUpperCase();
      if (pending && !r.channels.includes(pending)) {
        return {
          ...r,
          channels: [...r.channels, pending],
          channel_input: ""
        };
      }
      return { ...r, channel_input: "" };
    });

    setFormData(committed);

    for (let i = 0; i < committed.length; i++) {
      if (!validateRow(committed[i], i)) return;
    }

    const activeRows = committed.filter(
      (r) =>
        r.event_type.trim() ||
        (r.channels && r.channels.length) ||
        r.id
    );

    const types = activeRows.map((r) => r.event_type.trim().toUpperCase());
    const duplicateIndex = types.findIndex((t, i) => types.indexOf(t) !== i);

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Event Type",
        text: `Event Type "${activeRows[
          duplicateIndex
        ].event_type.trim()}" is used more than once.`
      });
      return;
    }

    const CONFIG = {
      headers: { "Content-Type": "application/json" }
    };

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(committed[0]);
        await axios.put(`${API_URL}${editingId}/`, payload, CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const r of activeRows) {
          await axios.post(API_URL, buildPayload(r), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} rule${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([emptyRule()]);

      await fetchRules();
    } catch (error) {
      console.error("Error saving notification rule:", error);

      let errorMessage = isEditing
        ? "Unable to update notification rule."
        : "Unable to create notification rules.";

      if (error.response?.data) {
        const d = error.response.data;
        if (typeof d === "string") errorMessage = d;
        else if (d.detail) errorMessage = d.detail;
        else if (d.message) errorMessage = d.message;
        else if (d.errors) {
          errorMessage = Object.entries(d.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(d);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([emptyRule()]);
  };

  // ---- Lookup helpers ----
  const getChannelsArray = (r) => {
    if (Array.isArray(r.channels)) return r.channels;
    if (typeof r.channels === "string" && r.channels) {
      try {
        const parsed = JSON.parse(r.channels);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return r.channels
          .split(",")
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean);
      }
    }
    return [];
  };

  const getEscalationSummary = (r) => {
    let obj = r.escalation_rules;
    if (typeof obj === "string") {
      try {
        obj = JSON.parse(obj);
      } catch {
        return "-";
      }
    }
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "-";
    const entries = Object.entries(obj);
    if (!entries.length) return "-";
    return entries.map(([k, v]) => `${k}: ${v}`).join(", ");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="notif-page">
          <SettingsTabs />

          {!showForm ? (
            <>
              <div className="notif-page-header">
                <div>
                  <h2>Notification Settings</h2>
                </div>

                <button
                  type="button"
                  className="notif-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Rule
                </button>
              </div>

              <div className="notif-list-card">
                {loading ? (
                  <div className="notif-loading">Loading rules...</div>
                ) : rules.length === 0 ? (
                  <div className="notif-empty">
                    <div className="notif-empty-icon">+</div>
                    <h3>No Notification Rules Found</h3>
                    <p>
                      There are no notification rules available for the current
                      tenant.
                    </p>
                    <button
                      type="button"
                      className="notif-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Rule
                    </button>
                  </div>
                ) : (
                  <div className="notif-table-wrapper">
                    <table className="notif-table">
                      <thead>
                        <tr>
                          <th>Event Type</th>
                          <th>Channels</th>
                          <th>Escalation Rules</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {rules.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <span className="notif-event-pill">
                                {r.event_type || "-"}
                              </span>
                            </td>

                            <td>
                              <div className="notif-pill-list">
                                {getChannelsArray(r).length === 0 ? (
                                  <span className="notif-muted">-</span>
                                ) : (
                                  getChannelsArray(r).map((c, i) => (
                                    <span key={i} className="notif-pill">
                                      {c}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td className="notif-muted notif-escalation-cell">
                              {getEscalationSummary(r)}
                            </td>

                            <td>
                              <div className="notif-actions">
                                <button
                                  type="button"
                                  className="notif-edit-btn"
                                  onClick={() => handleEdit(r)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="notif-delete-btn"
                                  onClick={() => handleDelete(r)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="notif-page-header">
                <div>
                  <h2>
                    {isEditing ? "Edit Notification Rule" : "Create Rules"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="notif-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {formData.map((r, index) => (
                  <div
                    className="notif-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="notif-form-body">
                      <div className="notif-record-header">
                        <h4>Rule {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="notif-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="notif-form-grid">
                        <div className="notif-form-group">
                          <label>
                            Event Type <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="event_type"
                            value={r.event_type}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. PROJECT_CREATED"
                            list="notif-event-type-suggestions"
                            maxLength={100}
                          />
                          <datalist id="notif-event-type-suggestions">
                            {EVENT_TYPE_SUGGESTIONS.map((v) => (
                              <option key={v} value={v} />
                            ))}
                          </datalist>
                        </div>

                        {/* Channels — free-text chip editor */}
                        <div className="notif-form-group">
                          <label>
                            Channels <span>*</span>
                          </label>

                          <div className="notif-channel-chips">
                            {r.channels.map((c) => (
                              <span
                                className="notif-channel-chip notif-channel-chip--active"
                                key={c}
                              >
                                <span>{c}</span>
                                <button
                                  type="button"
                                  className="notif-channel-chip-remove"
                                  onClick={() =>
                                    removeChannelChip(index, c)
                                  }
                                  aria-label={`Remove ${c}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}

                            <input
                              type="text"
                              className="notif-channel-input"
                              value={r.channel_input}
                              onChange={(e) =>
                                handleChannelInputChange(index, e.target.value)
                              }
                              onKeyDown={(e) =>
                                handleChannelKeyDown(index, e)
                              }
                              onBlur={() => addChannelChip(index)}
                              placeholder={
                                r.channels.length
                                  ? ""
                                  : "e.g. EMAIL"
                              }
                            />
                          </div>
                        </div>

                        {/* Escalation Rules — key/value rows */}
                        <div className="notif-form-group notif-form-group--full">
                          <label>Escalation Rules</label>

                          <div className="notif-escalation-rows">
                            {r.escalation_rows.map((row, rowIndex) => (
                              <div
                                className="notif-escalation-row"
                                key={rowIndex}
                              >
                                <input
                                  type="text"
                                  className="notif-escalation-key"
                                  value={row.key}
                                  onChange={(e) =>
                                    handleEscalationRowChange(
                                      index,
                                      rowIndex,
                                      "key",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g. after_minutes"
                                  maxLength={50}
                                />

                                <input
                                  type="text"
                                  className="notif-escalation-value"
                                  value={row.value}
                                  onChange={(e) =>
                                    handleEscalationRowChange(
                                      index,
                                      rowIndex,
                                      "value",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g. 60"
                                  maxLength={100}
                                />

                                {r.escalation_rows.length > 1 && (
                                  <button
                                    type="button"
                                    className="notif-escalation-remove"
                                    onClick={() =>
                                      removeEscalationRow(index, rowIndex)
                                    }
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}

                            <button
                              type="button"
                              className="notif-escalation-add"
                              onClick={() => addEscalationRow(index)}
                            >
                              + Add Escalation Rule
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="notif-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Rule
                  </button>
                )}

                <div className="notif-form-actions">
                  <button
                    type="button"
                    className="notif-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="notif-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Rule"
                      : "Save Rules"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantNotificationSettings;